import { useEffect, useState } from 'react';
import {
  useParams,
  useSearchParams,
  Link,
  useNavigate
} from 'react-router-dom';
import { useStripe } from '@stripe/react-stripe-js';
import { Handshake, CircleAlert } from 'lucide-react';

import { useCartCookie } from '@/helpers/useCartCookie';

import { Button } from '@/components/ui/button';
import Spinner from '@/components/common/Spinner';
import Error from '@/components/common/Error';

import { useProcessCart } from '@/data/cart';
import { useInvalidateAvailability } from '@/data/availability';

const PaymentConfirmation = () => {
  const { cartId } = useParams();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [cart, setCart] = useState(null);
  const [apiError, setApiError] = useState('');
  const stripe = useStripe();
  const processCart = useProcessCart();
  const invalidateAvailability = useInvalidateAvailability();
  const { removeCartId } = useCartCookie();
  const navigate = useNavigate();

  useEffect(() => {
    if (!stripe) {
      return;
    }

    const clientSecret = searchParams.get('payment_intent_client_secret');
    const paymentIntentId = searchParams.get('payment_intent');

    if (!clientSecret) {
      return;
    }

    const handlePaymentConfirmation = async () => {
      try {
        // 1. Check payment status with Stripe
        const { paymentIntent } =
          await stripe.retrievePaymentIntent(clientSecret);

        if (paymentIntent.status === 'succeeded') {
          const cart = await processCart(cartId, paymentIntentId);
          setCart(cart);
          removeCartId();
          setStatus('success');
          await invalidateAvailability();

          // Redirect after 2 seconds using useNavigate
          setTimeout(() => {
            navigate(`/order/${cart.orderId}`);
          }, 2000);
        } else if (paymentIntent.status === 'requires_payment_method') {
          // Payment failed
          setStatus('failed');
          //   navigate('/checkout?error=payment_failed');
        }
      } catch (error) {
        setApiError(error.message);
        setStatus('error');
      }
    };

    handlePaymentConfirmation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe, searchParams, cartId, navigate]);

  if (status === 'processing') {
    return (
      <div className="flex flex-col justify-center items-center h-[calc(100vh-320px)]">
        <Spinner className="w-16 h-16" />
        <h4 className="m-4">Processing your payment...</h4>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex flex-col justify-center items-center h-[calc(100vh-320px)]">
        <CircleAlert className="w-16 h-16" />
        <h4 className="m-4 text-center">Error processing your payment</h4>
        {apiError && <Error error={apiError} />}
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="flex flex-col justify-center items-center h-[calc(100vh-320px)]">
        <Handshake className="w-16 h-16" />
        <h4 className="m-4">Payment successful!</h4>
        <p className="m-4">Redirecting in 2 seconds...</p>
        <Link to={`/order/${cart.orderId}`}>
          <Button variant="outline">View Order</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-center items-center h-[calc(100vh-320px)]">
      <Spinner className="w-16 h-16" />
      <h4 className="m-4">Processing your payment...</h4>
    </div>
  );
};

export default PaymentConfirmation;
